/**
 * URL validation utilities for email verification and other URL-based flows
 */

import { SUPPORTED_LANGUAGES, LanguageCode } from './languageUtils';

export interface VerificationUrlState {
  isValid: boolean;
  errorType?: 'malformed' | 'expired' | 'used' | 'invalid';
  canRetry: boolean;
  originalUrl: string;
}

/**
 * Known malformed URL patterns that can result from double redirects
 * These patterns indicate URL corruption from language redirect issues
 */
const MALFORMED_PATTERNS = [
  '/esth/', '/enth/',  // Double redirect corruption patterns
  '/esauth/', '/enauth/',  // Alternative corruption patterns
  '/es/es/', '/en/en/',  // Duplicate language prefixes
  '/en/es/', '/es/en/'  // Mixed language prefixes
];

/**
 * Validates a verification URL for common malformation patterns
 * @param pathname - The URL pathname to validate
 * @returns VerificationUrlState indicating validity and error details
 */
export function validateVerificationUrl(pathname: string): VerificationUrlState {
  const originalUrl = pathname;

  // Check for malformed patterns from double redirects
  const isMalformed = MALFORMED_PATTERNS.some(pattern => pathname.includes(pattern));
  
  if (isMalformed) {
    return {
      isValid: false,
      errorType: 'malformed',
      canRetry: true,
      originalUrl
    };
  }

  // Check if URL has a valid language prefix
  const pathSegments = pathname.split('/').filter(Boolean);
  if (pathSegments.length > 0) {
    const firstSegment = pathSegments[0];
    const hasValidLanguage = SUPPORTED_LANGUAGES.includes(firstSegment as LanguageCode);
    
    // If first segment is not a valid language code and path is not root, it might be malformed
    // Exception: /auth paths are intentionally allowed without language prefix for backward compatibility
    // and as a fallback - the LanguageRouter will add the default language prefix automatically.
    // This ensures that:
    // 1. Old email links without language prefixes continue to work
    // 2. Direct navigation to /auth/verify defaults to Spanish gracefully
    // 3. The app always assumes a default language when none is specified
    if (!hasValidLanguage && pathname !== '/' && !pathname.startsWith('/auth')) {
      // Additional check: if it looks like it should have a language but doesn't
      if (pathSegments[0].length === 2) {
        return {
          isValid: false,
          errorType: 'malformed',
          canRetry: true,
          originalUrl
        };
      }
    }
  }

  // URL appears valid
  return {
    isValid: true,
    canRetry: false,
    originalUrl
  };
}

/**
 * Checks if a URL pathname appears to be corrupted from language redirect issues
 * @param pathname - The URL pathname to check
 * @returns True if the URL shows signs of corruption
 */
export function isCorruptedUrl(pathname: string): boolean {
  return MALFORMED_PATTERNS.some(pattern => pathname.includes(pattern));
}

/**
 * Attempts to extract the intended route from a corrupted URL
 * @param pathname - The potentially corrupted pathname
 * @returns The cleaned pathname or null if unable to determine
 */
export function extractIntendedRoute(pathname: string): string | null {
  // Try to identify and fix common corruption patterns
  for (const pattern of MALFORMED_PATTERNS) {
    if (pathname.includes(pattern)) {
      // Extract the part after the corrupted pattern
      const parts = pathname.split(pattern);
      if (parts.length > 1) {
        const routePart = parts[1];
        
        // For duplicate or mixed language prefixes like /es/es/ or /en/en/ or /en/es/
        // Remove both language prefixes entirely to get the clean route
        if (pattern === '/es/es/' || pattern === '/en/en/' || 
            pattern === '/en/es/' || pattern === '/es/en/') {
          return routePart.startsWith('/') ? routePart : `/${routePart}`;
        }
        
        // For other patterns, just add leading slash if needed
        return routePart.startsWith('/') ? routePart : `/${routePart}`;
      }
    }
  }
  
  return null;
}
